import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, X, ArrowLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function AdminTips() {
  const [tab, setTab] = useState("pending");
  const [tips, setTips] = useState([]);

  const load = async (s = tab) => {
    try {
      const r = await api.get("/tips", { params: { status: s } });
      setTips(r.data);
    } catch { toast.error("Failed to load tips"); }
  };
  useEffect(() => { load(); }, [tab]);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/tips/${id}`, { status });
      toast.success(`Tip ${status}`);
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-10">
      <Link to="/admin" className="inline-flex items-center gap-2 font-mono-plex text-xs uppercase tracking-wider hover:underline">
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </Link>
      <h1 className="font-display text-4xl font-black tracking-tighter mt-4">Tip Queue</h1>
      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="rounded-none bg-white border border-black/15">
          <TabsTrigger value="pending" data-testid="tab-pending" className="rounded-none">Pending</TabsTrigger>
          <TabsTrigger value="in_review" data-testid="tab-in-review" className="rounded-none">In review</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved" className="rounded-none">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected" className="rounded-none">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mt-6 space-y-3">
        {tips.length === 0 && <div className="text-[#555] text-center py-12 border border-dashed border-black/20">No tips in this queue.</div>}
        {tips.map((t) => (
          <div key={t.id} className="bg-white border border-black/15 p-5" data-testid={`tip-${t.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="font-mono-plex text-[11px] tracking-[0.2em] uppercase text-[#555]">
                  {t.created_at?.slice(0, 10)} · {t.category ? formatLabel(t.category) : "uncategorized"} · {t.jurisdiction || "—"}
                </div>
                <div className="font-display text-xl font-bold mt-1">{t.title}</div>
                <p className="text-sm text-[#222] mt-2 leading-relaxed">{t.description}</p>
                <AgentTriage tip={t} />
                {t.suggested_url && (
                  <a href={t.suggested_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs underline underline-offset-4">
                    Open source <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {(t.submitter_name || t.submitter_email) && (
                  <div className="mt-2 text-xs font-mono-plex uppercase tracking-wider text-[#555]">
                    via {t.submitter_name || "anonymous"}{t.submitter_email ? ` · ${t.submitter_email}` : ""}
                  </div>
                )}
              </div>
              {(tab === "pending" || tab === "in_review") && (
                <div className="flex gap-2 flex-wrap">
                  {tab === "pending" && (
                    <Button onClick={() => setStatus(t.id, "in_review")} variant="outline" className="rounded-none border-black h-9" data-testid={`in-review-${t.id}`}>
                      In review
                    </Button>
                  )}
                  <Button onClick={() => setStatus(t.id, "approved")} className="rounded-none bg-[#008A00] text-white h-9" data-testid={`approve-${t.id}`}>
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button onClick={() => setStatus(t.id, "rejected")} variant="outline" className="rounded-none border-[#D32F2F] text-[#D32F2F] h-9" data-testid={`reject-${t.id}`}>
                    <X className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const LABEL_COLORS = { promising: "#008A00", duplicate: "#0050FF", off_topic: "#D32F2F" };

function AgentTriage({ tip }) {
  if (!tip.agent_label && !tip.promoted_entry_id) return null;
  return (
    <div className="mt-3 border-l-2 pl-3 text-xs" style={{ borderColor: LABEL_COLORS[tip.agent_label] || "#000" }} data-testid={`tip-agent-triage-${tip.id}`}>
      {tip.agent_label && (
        <div className="font-mono-plex uppercase tracking-wider">
          AI agent: <b style={{ color: LABEL_COLORS[tip.agent_label] }}>{tip.agent_label.replace("_", "-")}</b>
          {tip.agent_confidence != null && ` · ${Math.round(tip.agent_confidence * 100)}%`}
        </div>
      )}
      {tip.agent_reason && <div className="text-[#555] mt-1">{tip.agent_reason}</div>}
      {tip.promoted_entry_id && (
        <Link to={`/admin/edit/${tip.promoted_entry_id}`} className="underline mt-1 inline-block" data-testid={`tip-promoted-link-${tip.id}`}>
          Promoted to draft →
        </Link>
      )}
    </div>
  );
}
